import * as THREE from 'three'

import flameVert from './shaders/flame/vertex.glsl'
import flameFrag from './shaders/flame/fragment.glsl'
import oilVert   from './shaders/tipOil/vertex.glsl'
import oilFrag   from './shaders/tipOil/fragment.glsl'
import sandVert  from './shaders/tipSand/vertex.glsl'
import sandFrag  from './shaders/tipSand/fragment.glsl'
import smokeVert from './shaders/tipSmoke/vertex.glsl'
import smokeFrag from './shaders/tipSmoke/fragment.glsl'
import waveVert  from './shaders/tipWave/vertex.glsl'
import waveFrag  from './shaders/tipWave/fragment.glsl'

const W = 260
const H = 200

/**
 * TipScenes
 * Attaches a mini Three.js scene to each .tip-canvas in the #tips section.
 *
 * Uses a SINGLE shared WebGLRenderer (preserveDrawingBuffer: true) to stay
 * within the browser's WebGL context limit. Each canvas receives pixels via
 * ctx2d.drawImage from the shared renderer's offscreen buffer.
 *
 * Card → scene mapping (data-scene attribute on canvas):
 *   flame  → 01 "Light one wick"    — particle flame with flicker
 *   oil    → 02 "Wait for Your Oil" — rippling golden oil pool
 *   sand   → 03 "Max 3 Hours"       — hourglass sand particles
 *   smoke  → 04 "Never Blow Out"    — wispy rising smoke
 *   wave   → 05 "Trim the Wick"     — rolling ivory wax waves
 */
export default class TipScenes {
    constructor() {
        this.activeScenes = new Set()

        // One shared WebGL renderer — not attached to the DOM.
        // preserveDrawingBuffer is required so drawImage reads correct pixels.
        this.renderer = new THREE.WebGLRenderer({
            antialias:             true,
            alpha:                 true,
            preserveDrawingBuffer: true,
        })
        this.renderer.setSize(W, H)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.setClearColor(0x000000, 0)
        this.renderer.outputColorSpace = THREE.SRGBColorSpace

        this.build()
        this.startLoop()
    }

    build() {
        document.querySelectorAll('.tip-canvas').forEach((canvas) => {
            this.buildScene(canvas, canvas.dataset.scene)
        })
    }

    buildScene(canvas, type) {
        // Turn canvas into a 2D blit target instead of a WebGL target
        canvas.width  = W
        canvas.height = H
        const ctx2d = canvas.getContext('2d')

        const scene  = new THREE.Scene()
        const camera = new THREE.PerspectiveCamera(50, W / H, 0.01, 100)

        const uniforms    = { uTime: { value: 0 } }
        const extraUpdate = []

        switch (type) {
            case 'flame': this.buildFlame(scene, camera, uniforms, extraUpdate); break
            case 'oil':   this.buildOil  (scene, camera, uniforms);              break
            case 'sand':  this.buildSand (scene, camera, uniforms);              break
            case 'smoke': this.buildSmoke(scene, camera, uniforms);              break
            case 'wave':  this.buildWave (scene, camera, uniforms);              break
            default: return
        }

        const sceneData = { scene, camera, uniforms, extraUpdate, ctx2d }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.activeScenes.add(sceneData)
            } else {
                this.activeScenes.delete(sceneData)
            }
        }, { threshold: 0.05 })

        observer.observe(canvas.closest('.tip-card') ?? canvas)
    }

    startLoop() {
        const gl = this.renderer.domElement

        const loop = (ts) => {
            this.activeScenes.forEach(({ scene, camera, uniforms, extraUpdate, ctx2d }) => {
                uniforms.uTime.value = ts * 0.001
                extraUpdate.forEach((fn) => fn(ts * 0.001))
                this.renderer.render(scene, camera)
                ctx2d.clearRect(0, 0, W, H)
                ctx2d.drawImage(gl, 0, 0, W, H)
            })
            requestAnimationFrame(loop)
        }
        requestAnimationFrame(loop)
    }

    static makePointsGeo(count, spread = [0.08, 0, 0.05]) {
        const pos     = new Float32Array(count * 3)
        const sizes   = new Float32Array(count)
        const offsets = new Float32Array(count)
        const randoms = new Float32Array(count * 3)

        for (let i = 0; i < count; i++) {
            pos[i * 3]     = (Math.random() - 0.5) * spread[0]
            pos[i * 3 + 1] = (Math.random() - 0.5) * (spread[1] ?? 0)
            pos[i * 3 + 2] = (Math.random() - 0.5) * spread[2]
            sizes[i]   = spread[3] + Math.random() * spread[4]
            offsets[i] = Math.random()
            randoms[i * 3]     = (Math.random() - 0.5) * 2
            randoms[i * 3 + 1] = Math.random()
            randoms[i * 3 + 2] = (Math.random() - 0.5) * 2
        }

        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
        geo.setAttribute('aSize',    new THREE.BufferAttribute(sizes, 1))
        geo.setAttribute('aOffset',  new THREE.BufferAttribute(offsets, 1))
        geo.setAttribute('aRandom',  new THREE.BufferAttribute(randoms, 3))
        return geo
    }

    buildFlame(scene, camera, uniforms, extraUpdate) {
        camera.position.set(0, 0.4, 2.6)
        camera.lookAt(0, 0.6, 0)

        uniforms.uFlicker = { value: 0.5 }
        uniforms.uFadeOut = { value: 1.0 }

        const geo = TipScenes.makePointsGeo(80, [0.10, 0, 0.07, 0.18, 0.55])

        const mat = new THREE.ShaderMaterial({
            vertexShader:   flameVert,
            fragmentShader: flameFrag,
            uniforms,
            transparent: true,
            depthWrite:  false,
            blending:    THREE.AdditiveBlending,
        })
        scene.add(new THREE.Points(geo, mat))

        extraUpdate.push((t) => {
            uniforms.uFlicker.value = 0.4
                + Math.sin(t * 7.3)  * 0.28
                + Math.sin(t * 13.1) * 0.12
        })
    }

    buildOil(scene, camera, uniforms) {
        camera.position.set(0, 2.2, 1.6)
        camera.lookAt(0, 0, 0)

        const geo = new THREE.PlaneGeometry(2.2, 1.7, 52, 40)
        geo.rotateX(-Math.PI * 0.5)

        const mat = new THREE.ShaderMaterial({
            vertexShader:   oilVert,
            fragmentShader: oilFrag,
            uniforms,
            transparent: true,
            depthWrite:  false,
            side:        THREE.DoubleSide,
        })
        scene.add(new THREE.Mesh(geo, mat))
    }

    buildSand(scene, camera, uniforms) {
        camera.position.set(0, 0, 2.8)
        camera.lookAt(0, 0, 0)

        const geo = TipScenes.makePointsGeo(160, [0, 0, 0, 0.12, 0.14])

        const mat = new THREE.ShaderMaterial({
            vertexShader:   sandVert,
            fragmentShader: sandFrag,
            uniforms,
            transparent: true,
            depthWrite:  false,
        })
        scene.add(new THREE.Points(geo, mat))
    }

    buildSmoke(scene, camera, uniforms) {
        camera.position.set(0, 0.2, 2.8)
        camera.lookAt(0, 0.5, 0)

        const geo = TipScenes.makePointsGeo(55, [0.05, 0, 0.03, 0.22, 0.55])

        const posAttr = geo.getAttribute('position')
        for (let i = 0; i < 55; i++) {
            posAttr.setY(i, -0.8)
        }
        posAttr.needsUpdate = true

        const mat = new THREE.ShaderMaterial({
            vertexShader:   smokeVert,
            fragmentShader: smokeFrag,
            uniforms,
            transparent: true,
            depthWrite:  false,
        })
        scene.add(new THREE.Points(geo, mat))
    }

    buildWave(scene, camera, uniforms) {
        camera.position.set(0, 1.5, 2.2)
        camera.lookAt(0, 0, 0)

        const geo = new THREE.PlaneGeometry(3.0, 2.2, 64, 44)
        geo.rotateX(-Math.PI * 0.5)

        const mat = new THREE.ShaderMaterial({
            vertexShader:   waveVert,
            fragmentShader: waveFrag,
            uniforms,
            transparent: true,
            side:        THREE.DoubleSide,
        })
        scene.add(new THREE.Mesh(geo, mat))
    }
}
